import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Outlet,
  useMatch,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useGetMeApi } from 'eds-common/api/v2';
import { AdminFilterBar } from '@data/components/report/AdminFilterBar/AdminFilterBar';
import {
  ALL_ORGANIZATIONS_LABEL,
  OrganizationDropdown,
} from '@data/components/report/AdminFilterBar/dropdowns/OrganizationDropdown/OrganizationDropdown';
import { TeacherDropdown } from '@data/components/report/AdminFilterBar/dropdowns/TeacherDropdown/TeacherDropdown';
import { AdminReportNav } from '@data/components/report/AdminReportNav/AdminReportNav';
import { Drilldown } from '@data/components/report/ReportFilterBar/ReportFilterBar';
import {
  useGetTeachersByOrgApi,
  type Teacher,
} from '@data/hooks/api/use-get-teachers-by-org-api/use-get-teachers-by-org-api';
import { ClassDropdown } from '@data/components/report/ReportFilterBar/dropdowns/ClassDropdown/ClassDropdown';
import { StudentDropdown } from '@data/components/report/ReportFilterBar/dropdowns/StudentDropdown/StudentDropdown';
import { useGetClassesByEdAppApi } from '@data/hooks/api/use-get-classes-by-ed-app-api/use-get-classes-by-ed-app-api';
import { useGetStudentsByClassAndEdAppApi } from '@data/hooks/api/use-get-students-by-class-and-ed-app-api/use-get-students-by-class-and-ed-app-api';
import { useApplicationsHierarchy } from '@data/hooks/api/use-applications-hierarchy-api/use-applications-hierarchy-api';
import { useOrgOptions } from '@data/hooks/use-org-label/use-org-label';
import {
  useOrgHierarchy,
  type OrgOption,
} from '@data/hooks/api/use-org-hierarchy-api/use-org-hierarchy-api';
import {
  SCHOOL_YEAR_VALUE,
  type DateRange,
} from '@data/components/report/ReportFilterBar/dropdowns/DateRangeDropdown/DateRangeDropdown';
import { format } from 'date-fns';
import { getSchoolYearDateRange } from '@data/utils/get-school-year';
import {
  DATE_PARAM_FORMAT,
  resolveDateRange,
} from '@data/utils/resolve-date-range';
import type { ProductCode } from '@data/config/reports';

const ORG_ID_KEY = 'orgId';
const TEACHER_ID_KEY = 'teacherId';
const CLASS_ID_KEY = 'classId';
const STUDENT_ID_KEY = 'studentId';
const FROM_KEY = 'from';
const TO_KEY = 'to';

export interface AdminReportOutletContext {
  productId: ProductCode;
  dateRange: DateRange;
  /** Every organization the report covers — never empty. */
  orgIds: string[];
  /** What to call that scope in a heading — an org name, or "All Organizations". */
  selectedOrgLabel: string;
  /**
   * The same scope named without the educator label: product access belongs to
   * the whole organization, not to the educators attached to it directly.
   */
  accessOrgLabel: string;
  /** True until the product-scoped hierarchy answers; `orgIds` is not final yet. */
  isScopeLoading: boolean;
  /** Narrows the organization report to these educators; empty means all. */
  teacherIds: string[];
  /**
   * How many organizations the filter names, which is not the same as how many
   * the report covers: `orgIds` holds only those that also hold the product.
   * Zero means the whole hierarchy, as an empty class selection does.
   */
  selectedOrgCount: number;
  /** True on `overview/org`, the single-organization drill-down. */
  isOrgDrilldown: boolean;
  /** True on `overview/teacher`, the single-educator drill-down. */
  isTeacherDrilldown: boolean;
  /** True on `overview/class`, the single-class drill-down. */
  isClassDrilldown: boolean;
  /** True on `overview/student`, the single-student drill-down. */
  isStudentDrilldown: boolean;
  /** What to call the student in a heading, on the student drill-down. */
  selectedStudentLabel: string;
  /** Narrows the class report to these students; empty means all of them. */
  studentIds: string[];
  /** The classes the educator drill-down is narrowed to; empty means all. */
  classIds: string[];
  /** How many classes the educator drill-down covers; undefined while loading. */
  classCount?: number;
  /** What to call the educator in a heading, on the educator drill-down. */
  selectedTeacherLabel: string;
  /** What to call the class in a heading, on the class drill-down. */
  selectedClassLabel: string;
  /** Where the heading's back control leads, when there is somewhere to go. */
  backTarget?: { label: string; to: string };
}

export function useAdminReportOutletContext() {
  return useOutletContext<AdminReportOutletContext>();
}

/** Only a non-default range is on the URL, so the default carries nothing. */
function dateSearch(from: string | null, to: string | null) {
  const search = new URLSearchParams();
  if (from && to) {
    search.set(FROM_KEY, from);
    search.set(TO_KEY, to);
  }
  return search;
}

/**
 * Where the report's back control leads: an educator steps back to their
 * organization, an organization to all of them — but only when there is more
 * than one to return to.
 */
function buildBackTarget({
  isStudentReport,
  isClassReport,
  isTeacherReport,
  isOrgReport,
  orgOptions,
  orgId,
  teacherId,
  teacherLabel,
  classId,
  classLabel,
  productId,
  dates,
}: {
  isStudentReport: boolean;
  isClassReport: boolean;
  isTeacherReport: boolean;
  isOrgReport: boolean;
  orgOptions: OrgOption[];
  orgId: string | undefined;
  /** The educator the class level steps back to. */
  teacherId: string | undefined;
  teacherLabel: string;
  /** The class the student level steps back to. */
  classId: string;
  classLabel: string;
  productId: string;
  dates: URLSearchParams;
}) {
  if (isStudentReport && orgId && teacherId && classId) {
    const search = new URLSearchParams(dates);
    search.set(ORG_ID_KEY, orgId);
    search.set(TEACHER_ID_KEY, teacherId);
    search.set(CLASS_ID_KEY, classId);
    return {
      label: `Back to ${classLabel}`,
      to: `/data/${productId}/overview/${Drilldown.Class}?${search.toString()}`,
    };
  }

  if (isClassReport && orgId && teacherId) {
    const search = new URLSearchParams(dates);
    search.set(ORG_ID_KEY, orgId);
    search.set(TEACHER_ID_KEY, teacherId);
    return {
      label: `Back to ${teacherLabel}`,
      to: `/data/${productId}/overview/${
        Drilldown.Teacher
      }?${search.toString()}`,
    };
  }

  if (isTeacherReport && orgId) {
    const search = new URLSearchParams(dates);
    search.set(ORG_ID_KEY, orgId);
    const orgName = orgOptions.find((org) => org.orgId === orgId)?.orgName;
    return {
      label: `Back to ${orgName ?? 'organization'}`,
      to: `/data/${productId}/overview/${
        Drilldown.Organization
      }?${search.toString()}`,
    };
  }

  if (isOrgReport && orgOptions.length > 1) {
    return {
      label: `Back to ${ALL_ORGANIZATIONS_LABEL}`,
      to: `/data/${productId}/overview?${dates.toString()}`,
    };
  }

  return undefined;
}

function buildTeacherLabel(
  teachers: Teacher[] | undefined,
  teacherId: string | undefined
) {
  const teacher = (teachers ?? []).find(
    (candidate) => candidate.userId === teacherId
  );
  if (!teacher) return 'Educator';
  return [teacher.firstName, teacher.middleName, teacher.lastName]
    .filter(Boolean)
    .join(' ');
}

/** Counts the selection, not the effective ids — the reader picked it either way. */
function buildOrgLabel({
  selectedOrgIds,
  orgOptions,
  effectiveOrgIds,
  productOrgsLoading,
}: {
  selectedOrgIds: string[];
  orgOptions: OrgOption[];
  effectiveOrgIds: string[];
  productOrgsLoading: boolean;
}) {
  const label = (() => {
    if (selectedOrgIds.length === 0) return ALL_ORGANIZATIONS_LABEL;
    if (selectedOrgIds.length === 1) {
      return (
        orgOptions.find((org) => org.orgId === selectedOrgIds[0])?.orgName ??
        'Organization'
      );
    }
    return `${selectedOrgIds.length} Organizations`;
  })();

  // Not known until the product-scoped hierarchy answers.
  if (productOrgsLoading) return label;

  // How many the reader is asking about, against how many the figures can
  // cover. Named only when some of them do not hold this product — when they
  // all do, the count would only repeat the label, and when none do the report
  // is replaced by the no-access view, which names the organization plainly.
  const asked =
    selectedOrgIds.length > 0 ? selectedOrgIds.length : orgOptions.length;
  if (effectiveOrgIds.length === 0 || effectiveOrgIds.length >= asked) {
    return label;
  }

  return `${label}, ${effectiveOrgIds.length} with product access`;
}

/**
 * The filters, which live on the URL so a filtered report can be linked and
 * reloaded. The date range is mirrored in state as well, since only a
 * non-default range is written to the URL.
 */
function useFilterParams({
  searchParams,
  setSearchParams,
  isStudentReport,
  scopesOneClass,
  scopesOneTeacher,
  scopesOneOrg,
}: {
  searchParams: URLSearchParams;
  setSearchParams: ReturnType<typeof useSearchParams>[1];
  isStudentReport: boolean;
  scopesOneClass: boolean;
  scopesOneTeacher: boolean;
  scopesOneOrg: boolean;
}) {
  const updateParams = useCallback(
    (updates: Record<string, string | string[] | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([key, value]) => {
            next.delete(key);
            if (value !== null) {
              const values = Array.isArray(value) ? value : [value];
              values.filter(Boolean).forEach((v) => next.append(key, v));
            }
          });
          return next;
        },
        // Replace, so filtering does not stack history entries.
        { replace: true, preventScrollReset: true }
      );
    },
    [setSearchParams]
  );

  const fromParam = searchParams.get(FROM_KEY);
  const toParam = searchParams.get(TO_KEY);

  const [dateRange, setDateRange] = useState<DateRange>(() =>
    resolveDateRange(fromParam, toParam)
  );
  const [dateResetKey, setDateResetKey] = useState(0);

  // Skips the initial mount, already seeded from the URL above.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setDateRange(resolveDateRange(fromParam, toParam));
  }, [fromParam, toParam]);
  const handleDateRangeChange = useCallback(
    (range: DateRange, optionValue: string) => {
      setDateRange(range);
      // The school-year default carries no date on the URL.
      updateParams(
        optionValue === SCHOOL_YEAR_VALUE
          ? { [FROM_KEY]: null, [TO_KEY]: null }
          : {
              [FROM_KEY]: format(range.from, DATE_PARAM_FORMAT),
              [TO_KEY]: format(range.to, DATE_PARAM_FORMAT),
            }
      );
    },
    [updateParams]
  );

  const setOrgIdParams = useCallback(
    // Changing organization drops the teacher filter: the ids belong to the
    // organization that was being read.
    (orgIds: string[]) =>
      updateParams({ [ORG_ID_KEY]: orgIds, [TEACHER_ID_KEY]: null }),
    [updateParams]
  );

  const setTeacherIdParams = useCallback(
    // The classes belong to the educator being read.
    (teacherIds: string[]) =>
      updateParams({ [TEACHER_ID_KEY]: teacherIds, [CLASS_ID_KEY]: null }),
    [updateParams]
  );

  const setClassIdParams = useCallback(
    // The students belong to the class being read.
    (classIds: string[]) =>
      updateParams({ [CLASS_ID_KEY]: classIds, [STUDENT_ID_KEY]: null }),
    [updateParams]
  );

  const setStudentIdParams = useCallback(
    (studentIds: string[]) => updateParams({ [STUDENT_ID_KEY]: studentIds }),
    [updateParams]
  );

  const handleReset = useCallback(() => {
    // A single student is fixed in place, so there is nothing to reset.
    if (isStudentReport) return;

    setDateRange(getSchoolYearDateRange());
    // Remount the dropdown so its own selection returns to the default too.
    setDateResetKey((key) => key + 1);
    updateParams({
      [FROM_KEY]: null,
      [TO_KEY]: null,
      [STUDENT_ID_KEY]: null,
      // The class level requires its class, as the levels above require theirs.
      ...(scopesOneClass ? {} : { [CLASS_ID_KEY]: null }),
      // A drill-down is about one organization, and its route requires one, so
      // reset clears the date and the filters below it. The educator is
      // likewise required by the educator drill-down.
      ...(scopesOneTeacher ? {} : { [TEACHER_ID_KEY]: null }),
      ...(scopesOneOrg ? {} : { [ORG_ID_KEY]: null }),
    });
  }, [
    updateParams,
    isStudentReport,
    scopesOneClass,
    scopesOneTeacher,
    scopesOneOrg,
  ]);

  return {
    dateRange,
    dateResetKey,
    hasUrlDateRange: !!fromParam && !!toParam,
    fromParam,
    toParam,
    handleDateRangeChange,
    setOrgIdParams,
    setTeacherIdParams,
    setClassIdParams,
    setStudentIdParams,
    handleReset,
  };
}

/** The class filter, which only the educator drill-down offers. */
function ClassFilter({
  orgId,
  teacherId,
  productId,
  productCodes,
  selectedClassIds,
  single,
  disabled,
  onChange,
}: {
  orgId: string;
  teacherId: string | undefined;
  productId: string;
  productCodes: string[] | undefined;
  selectedClassIds: string[];
  /** One class at a time, for a report about a single class. */
  single: boolean;
  /** Fixed to the class being read, with no choice to make. */
  disabled: boolean;
  onChange: (classIds: string[]) => void;
}) {
  if (!orgId || !teacherId || !productCodes) return null;

  const shared = {
    orgId,
    userId: teacherId,
    edApp: productId as ProductCode,
    productCodes,
  };

  return (
    <div className="w-full tablet:w-[280px]">
      {single ? (
        <ClassDropdown
          {...shared}
          mode="single"
          disabled={disabled}
          selectedClassId={selectedClassIds[0] ?? ''}
          onChange={(classId: string) => onChange([classId])}
        />
      ) : (
        <ClassDropdown
          {...shared}
          mode="multiselect"
          selectedClassIds={selectedClassIds}
          onChange={onChange}
        />
      )}
    </div>
  );
}

/** The students in the class being read; only the class level has them. */
function StudentFilter({
  classId,
  productId,
  productCodes,
  selectedStudentIds,
  single,
  onChange,
}: {
  classId: string;
  productId: string;
  productCodes: string[] | undefined;
  selectedStudentIds: string[];
  /** One student at a time, for a report about a single student. */
  single: boolean;
  onChange: (studentIds: string[]) => void;
}) {
  if (!classId || !productCodes) return null;

  const shared = {
    classId,
    edApp: productId as ProductCode,
    productCodes,
  };

  return (
    <div className="w-full tablet:w-[280px]">
      {single ? (
        <StudentDropdown
          {...shared}
          mode="single"
          selectedStudentId={selectedStudentIds[0] ?? ''}
          onChange={(studentId: string) => onChange([studentId])}
        />
      ) : (
        <StudentDropdown
          {...shared}
          mode="multiselect"
          selectedStudentIds={selectedStudentIds}
          onChange={onChange}
        />
      )}
    </div>
  );
}

/**
 * What the figures actually cover: the chosen organizations narrowed to the
 * ones holding this product, or all of them — and likewise every educator in
 * the organization when none is picked, the way an empty organization filter
 * means every organization.
 */
function resolveScope({
  selectedOrgIds,
  productOrgs,
  selectedTeacherIds,
  teachers,
}: {
  selectedOrgIds: string[];
  productOrgs: OrgOption[] | undefined;
  selectedTeacherIds: string[];
  teachers: Teacher[] | undefined;
}) {
  const productOrgOptions = productOrgs ?? [];
  const productOrgIds = new Set(productOrgOptions.map((org) => org.orgId));

  return {
    effectiveOrgIds:
      selectedOrgIds.length > 0
        ? selectedOrgIds.filter((orgId) => productOrgIds.has(orgId))
        : productOrgOptions.map((org) => org.orgId),
    effectiveTeacherIds:
      selectedTeacherIds.length > 0
        ? selectedTeacherIds
        : (teachers ?? []).map((teacher) => teacher.userId),
  };
}

/**
 * The one id a narrower filter hangs off — the organization its educators come
 * from, the class its students come from. Empty until the level that scopes to
 * it has it on the URL.
 */
function scopedId(scopesToOne: boolean, selected: string[]) {
  if (!scopesToOne) return '';
  return selected[0] ?? '';
}

/** Named as the heading shows them — given name first, family name last. */
function buildStudentLabel(
  students:
    | { id: string; given_name: string; family_name: string }[]
    | undefined,
  studentId: string | undefined
) {
  const student = (students ?? []).find(
    (candidate) => candidate.id === studentId
  );
  if (!student) return 'Student';
  return [student.given_name, student.family_name].filter(Boolean).join(' ');
}

function buildClassLabel(
  classes: { classId: string; name: string }[] | undefined,
  classId: string
) {
  return (
    (classes ?? []).find((cls) => cls.classId === classId)?.name ?? 'Class'
  );
}

/** The chosen classes, or every class the educator holds for this product. */
function countClasses(
  selectedClassIds: string[],
  classes: { classId: string }[] | undefined
) {
  if (selectedClassIds.length > 0) return selectedClassIds.length;
  return classes?.length;
}

/**
 * Which admin level the URL is on. Each narrows the one above it, so the
 * flags are read together rather than re-derived wherever one is needed.
 */
function useReportLevel() {
  return {
    // One organization, with a teacher filter.
    isOrgReport:
      useMatch(`/data/:productId/overview/${Drilldown.Organization}`) !== null,
    // One organization, one educator, their classes.
    isTeacherReport:
      useMatch(`/data/:productId/overview/${Drilldown.Teacher}`) !== null,
    // One class of that educator, and its students.
    isClassReport:
      useMatch(`/data/:productId/overview/${Drilldown.Class}`) !== null,
    // One student of that class.
    isStudentReport:
      useMatch(`/data/:productId/overview/${Drilldown.Student}`) !== null,
  };
}

/**
 * Which level the reader is on, read off the flags the report itself reads.
 * The only definition of that answer: the nav, which keeps a product change on
 * this level, and the report, which decides what to show, must not disagree.
 */
export function currentDrilldown({
  isOrgDrilldown,
  isTeacherDrilldown,
  isClassDrilldown,
  isStudentDrilldown,
}: Pick<
  AdminReportOutletContext,
  | 'isOrgDrilldown'
  | 'isTeacherDrilldown'
  | 'isClassDrilldown'
  | 'isStudentDrilldown'
>): Drilldown {
  if (isStudentDrilldown) return Drilldown.Student;
  if (isClassDrilldown) return Drilldown.Class;
  if (isTeacherDrilldown) return Drilldown.Teacher;
  return isOrgDrilldown ? Drilldown.Organization : Drilldown.AllOrganizations;
}

/** Everything the report below needs, in one object per render. */
function buildOutletContext({
  productId,
  dateRange,
  effectiveOrgIds,
  selectedOrgCount,
  selectedOrgLabel,
  accessOrgLabel,
  productOrgsLoading,
  effectiveTeacherIds,
  selectedClassIds,
  selectedStudentIds,
  classCount,
  selectedTeacherLabel,
  selectedClassLabel,
  selectedStudentLabel,
  backTarget,
  level,
}: {
  productId: ProductCode;
  dateRange: DateRange;
  effectiveOrgIds: string[];
  selectedOrgCount: number;
  selectedOrgLabel: string;
  accessOrgLabel: string;
  productOrgsLoading: boolean;
  effectiveTeacherIds: string[];
  selectedClassIds: string[];
  selectedStudentIds: string[];
  classCount: number | undefined;
  selectedTeacherLabel: string;
  selectedClassLabel: string;
  selectedStudentLabel: string;
  backTarget: { label: string; to: string } | undefined;
  level: ReturnType<typeof useReportLevel>;
}): AdminReportOutletContext {
  return {
    productId,
    dateRange,
    orgIds: effectiveOrgIds,
    selectedOrgCount,
    selectedOrgLabel,
    accessOrgLabel,
    isScopeLoading: productOrgsLoading,
    teacherIds: effectiveTeacherIds,
    classIds: selectedClassIds,
    studentIds: selectedStudentIds,
    ...(classCount === undefined ? {} : { classCount }),
    selectedTeacherLabel,
    selectedClassLabel,
    selectedStudentLabel,
    isOrgDrilldown: level.isOrgReport,
    isTeacherDrilldown: level.isTeacherReport,
    isClassDrilldown: level.isClassReport,
    isStudentDrilldown: level.isStudentReport,
    ...(backTarget ? { backTarget } : {}),
  };
}

/** The admin report shell: filter card, application nav, report panel. */
export function AdminReportLayout() {
  const { productId } = useParams<{ productId: string }>();
  const { isOrgReport, isTeacherReport, isClassReport, isStudentReport } =
    useReportLevel();
  const scopesOneClass = isClassReport || isStudentReport;
  const scopesOneTeacher = isTeacherReport || scopesOneClass;
  const scopesOneOrg = isOrgReport || scopesOneTeacher;
  const { data: user } = useGetMeApi();
  // Unscoped by product, so switching product never drops a selection out of the list.
  const { data: orgs } = useOrgHierarchy();
  // Labelled for the filter and the headings; the plain list is below.
  const { data: labelledOrgOptions } = useOrgOptions();

  // Narrowed to this product: only these organizations have figures to report for it.
  const { data: productOrgs, isLoading: productOrgsLoading } = useOrgHierarchy({
    appCode: productId,
  });

  // Filters live on the URL so a filtered report can be linked and reloaded.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedOrgIds = searchParams.getAll(ORG_ID_KEY);
  const selectedTeacherIds = searchParams.getAll(TEACHER_ID_KEY);
  const selectedClassIds = searchParams.getAll(CLASS_ID_KEY);
  const selectedStudentIds = searchParams.getAll(STUDENT_ID_KEY);

  const teacherOrgId = scopedId(scopesOneOrg, selectedOrgIds);
  // The class the student filter belongs to; only the class level has one.
  const studentClassId = scopedId(scopesOneClass, selectedClassIds);
  const { data: teachers } = useGetTeachersByOrgApi(teacherOrgId, {
    retry: 0,
  });

  // What the hierarchy holds, not what the admin is entitled to: an admin need
  // not hold the product they are reporting on.
  const { data: hierarchyApplications } = useApplicationsHierarchy();
  const productCodes = hierarchyApplications
    ?.find((application) => application.applicationCode === productId)
    ?.products.map((product) => product.productCode);

  // Shares its query with the class filter below, so the count costs no
  // request. Both levels that have an educator need it: the educator level to
  // count their classes, the class level to name the one being read.
  const { data: teacherClasses } = useGetClassesByEdAppApi(
    {
      orgId: scopesOneTeacher ? teacherOrgId : '',
      userId: selectedTeacherIds[0] ?? '',
      edApp: productId ?? '',
      productCodes,
    },
    { retry: 0 }
  );

  // Shares its query with the student filter, so the name costs no request.
  const { data: classStudents } = useGetStudentsByClassAndEdAppApi(
    {
      classId: studentClassId,
      edApp: productId ?? '',
      productCodes,
    },
    { retry: 0 }
  );

  const {
    dateRange,
    dateResetKey,
    hasUrlDateRange,
    fromParam,
    toParam,
    handleDateRangeChange,
    setOrgIdParams,
    setTeacherIdParams,
    setClassIdParams,
    setStudentIdParams,
    handleReset,
  } = useFilterParams({
    searchParams,
    setSearchParams,
    isStudentReport,
    scopesOneClass,
    scopesOneTeacher,
    scopesOneOrg,
  });

  if (!productId) return null;

  const orgOptions = orgs ?? [];
  const { effectiveOrgIds, effectiveTeacherIds } = resolveScope({
    selectedOrgIds,
    productOrgs,
    selectedTeacherIds,
    teachers,
  });

  const selectedTeacherLabel = buildTeacherLabel(
    teachers,
    selectedTeacherIds[0]
  );
  const selectedClassLabel = buildClassLabel(teacherClasses, studentClassId);
  const selectedStudentLabel = buildStudentLabel(
    classStudents,
    selectedStudentIds[0]
  );

  const backTarget = buildBackTarget({
    isStudentReport,
    isClassReport,
    isTeacherReport,
    isOrgReport,
    orgOptions: labelledOrgOptions,
    orgId: selectedOrgIds[0],
    teacherId: selectedTeacherIds[0],
    teacherLabel: selectedTeacherLabel,
    classId: studentClassId,
    classLabel: selectedClassLabel,
    productId,
    dates: dateSearch(fromParam, toParam),
  });

  const classCount = countClasses(selectedClassIds, teacherClasses);

  const selectedOrgLabel = buildOrgLabel({
    selectedOrgIds,
    orgOptions: labelledOrgOptions,
    effectiveOrgIds,
    productOrgsLoading,
  });

  const accessOrgLabel = buildOrgLabel({
    selectedOrgIds,
    orgOptions,
    effectiveOrgIds,
    productOrgsLoading,
  });

  // Built once: the nav reads the reader's level off the same object the
  // report below does, so the two cannot drift apart.
  const outletContext = buildOutletContext({
    productId: productId as ProductCode,
    dateRange,
    effectiveOrgIds,
    selectedOrgCount: selectedOrgIds.length,
    selectedOrgLabel,
    accessOrgLabel,
    productOrgsLoading,
    effectiveTeacherIds,
    selectedClassIds,
    selectedStudentIds,
    classCount,
    selectedTeacherLabel,
    selectedClassLabel,
    selectedStudentLabel,
    backTarget,
    level: { isOrgReport, isTeacherReport, isClassReport, isStudentReport },
  });
  const reportDrilldown = currentDrilldown(outletContext);

  return (
    <div className="min-h-[calc(100vh-80px)] min-w-0 flex-1 bg-brand-grey_7 antialiased">
      <div className="mx-auto flex w-full min-w-0 max-w-[1440px] flex-col py-6 mobile:px-4 tablet:px-10">
        <AdminFilterBar
          orgName={user?.currentOrg?.orgName}
          dateRange={dateRange}
          dateResetKey={dateResetKey}
          initialCustomRange={hasUrlDateRange ? dateRange : null}
          onDateRangeChange={handleDateRangeChange}
          organizationFilter={
            <OrganizationDropdown
              selectedOrgIds={selectedOrgIds}
              onChange={setOrgIdParams}
              appCode={productId}
              mode={scopesOneOrg ? 'single' : 'multiselect'}
              disabled={scopesOneTeacher}
            />
          }
          teacherFilter={
            <TeacherDropdown
              orgId={teacherOrgId}
              selectedTeacherIds={selectedTeacherIds}
              onChange={setTeacherIdParams}
              mode={scopesOneTeacher ? 'single' : 'multiselect'}
              disabled={scopesOneClass}
            />
          }
          classFilter={
            scopesOneTeacher && (
              <ClassFilter
                orgId={teacherOrgId}
                teacherId={selectedTeacherIds[0]}
                productId={productId}
                productCodes={productCodes}
                selectedClassIds={selectedClassIds}
                single={scopesOneClass}
                disabled={isStudentReport}
                onChange={setClassIdParams}
              />
            )
          }
          studentFilter={
            scopesOneClass && (
              <StudentFilter
                classId={studentClassId}
                productId={productId}
                productCodes={productCodes}
                selectedStudentIds={selectedStudentIds}
                single={isStudentReport}
                onChange={setStudentIdParams}
              />
            )
          }
          onReset={handleReset}
        />

        {/* tablet:items-start stops the report panel stretching to the nav's height. */}
        <div className="flex w-full min-w-0 flex-col laptop:flex-row laptop:items-start laptop:gap-6">
          {/* Below laptop the nav is a drawer the report header opens. */}
          <div className="hidden laptop:block">
            <AdminReportNav drilldown={reportDrilldown} />
          </div>

          <div className="flex min-w-0 flex-1 flex-col rounded-lg bg-white p-6 mobile:mt-4 laptop:mt-0">
            <main className="min-w-0">
              <Outlet context={outletContext} />
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}
